import { AxiosError } from 'axios';
import { CommandResult, FormApiService, ItemCommandResult, QueryApiService } from 'src/common/api-service';
import {
  CreateMatchMeetingFromEnterprise,
  Match,
  MatchMeeting,
  MatchMeetingRejectionReason,
  RejectMatchFromEnterpriseBody,
  SelectMatchFromEnterpriseBody,
  RequestMeetingReschedule,
  RespondMeetingReschedule
} from './types';

export class MatchApiService extends QueryApiService<Match> {
  constructor() {
    super('matches');
  }
}

export class MatchMeetingApiService extends QueryApiService<MatchMeeting> {
  constructor() {
    super('matchMeetings');
  }
}

export class MatchFormApiService extends FormApiService {
  constructor() {
    super('matches');
  }

  public async updateMatchFavoriteFromEnterprise(matchId: string, favorite: boolean): Promise<CommandResult> {
    try {
      return await this.put({ favorite }, `${matchId}/enterprise/favorite`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async rejectMatchFromEnterprise(matchId: string, body?: RejectMatchFromEnterpriseBody): Promise<CommandResult> {
    try {
      return await this.put(body ?? {}, `${matchId}/enterprise/reject`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async acceptMatchFromEnterprise(matchId: string): Promise<CommandResult> {
    try {
      return await this.put({}, `${matchId}/enterprise/accept`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async selectMatchFromEnterprise(matchId: string, body: SelectMatchFromEnterpriseBody): Promise<ItemCommandResult<string>> {
    try {
      return await this.put(body, `${matchId}/enterprise/select`);
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<string>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async rejectMatchFromPerson(
    matchId: string,
    body: { meetingId?: string; reason: MatchMeetingRejectionReason; comment?: string }
  ): Promise<CommandResult> {
    try {
      return await this.put(body, `${matchId}/person/reject`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async deleteUnavailableMatchFromPerson(matchId: string): Promise<CommandResult> {
    try {
      return await this.put({}, `${matchId}/person/deleteUnavailable`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async createMatchMeetingFromEnterprise(matchId: string, body: CreateMatchMeetingFromEnterprise): Promise<CommandResult> {
    try {
      return await this.post(body, `${matchId}/enterprise/meeting`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }
}

export class MatchMeetingFormApiService extends FormApiService {
  constructor() {
    super('matchMeetings');
  }

  public async rejectMeetingFromPerson(meetingId: string, body: { comment?: string; reason: MatchMeetingRejectionReason }): Promise<CommandResult> {
    try {
      return await this.put(body, `${meetingId}/person/reject`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async cancelMeetingFromPerson(meetingId: string, body: { comment?: string }): Promise<CommandResult> {
    try {
      return await this.put(body, `${meetingId}/person/cancel`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async confirmMeetingFromPerson(meetingId: string, body: { comment?: string }): Promise<CommandResult> {
    try {
      return await this.put(body, `${meetingId}/person/confirm`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async cancelMeetingFromEnterprise(meetingId: string, body: { comment?: string }): Promise<CommandResult> {
    try {
      return await this.put(body, `${meetingId}/enterprise/cancel`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async requestRescheduleFromPerson(meetingId: string, body: RequestMeetingReschedule): Promise<CommandResult> {
    try {
      return await this.put(body, `${meetingId}/person/requestReschedule`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async respondRescheduleFromPerson(meetingId: string, body: RespondMeetingReschedule): Promise<CommandResult> {
    try {
      return await this.put(body, `${meetingId}/person/respondReschedule`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async requestRescheduleFromEnterprise(meetingId: string, body: RequestMeetingReschedule): Promise<CommandResult> {
    try {
      return await this.put(body, `${meetingId}/enterprise/requestReschedule`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async respondRescheduleFromEnterprise(meetingId: string, body: RespondMeetingReschedule): Promise<CommandResult> {
    try {
      return await this.put(body, `${meetingId}/enterprise/respondReschedule`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async cancelRescheduleRequest(meetingId: string, body: { requestId: string }): Promise<CommandResult> {
    try {
      return await this.put(body, `${meetingId}/cancelRescheduleRequest`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }
}
